<script>
import { mapState, mapGetters } from 'vuex';
import eventHub from 'viralstyle-ui/src/eventHub';
import vueMixins from 'viralstyle-ui/src/vueMixins';

import clientMixins from 'utils/client-mixins';
import { privileges } from 'domains/base/_base-enum';

export default {
  mixins: [clientMixins, vueMixins],
  props: {
    ready: [Boolean, String],
  },
  data: () => ({
    privileges,
    redirectTimeout: null,
  }),
  computed: {
    ...mapState({
      processing: state => state.processing,
      currentUser: state => state.currentUser,
      navMenuOpen: state => state.navMenuOpen,
      showMyOrdersLink: state => state.showMyOrdersLink,
    }),
    ...mapGetters(['permissionViewPayouts', 'permissionAccessDomains']),
  },
  async created() {
    eventHub.$on('prevent-redirect', this.$_handlePreventRedirect);
    eventHub.$on('confirm-redirect', this.$_handleConfirmRedirect);
  },
  beforeDestroy() {
    eventHub.$off('prevent-redirect', this.$_handlePreventRedirect);
    eventHub.$off('confirm-redirect', this.$_handleConfirmRedirect);
  },
  methods: {
    $_handlePreventRedirect() {
      clearTimeout(this.redirectTimeout);
    },
    $_handleConfirmRedirect(e) {
      if (e === 'create') this.$_performRedirect();
    },
    $_performRedirect() {
      window.location.href = this.createLink;
    },
    toggleMenu() {
      this.$store.commit('toggleNavMenu', !this.navMenuOpen);
    },
    closeMenu() {
      this.$store.commit('toggleNavMenu', false);
    },
    navigate(name) {
      if (!this.processing) {
        this.$router.push({ name });
        this.closeMenu();
      }
    },
    handleLogout() {
      this.$store.dispatch('logout');
    },
    handleClickCreateCampaign() {
      this.closeMenu();

      this.redirectTimeout = setTimeout(() => {
        this.$_performRedirect();
      }, 100);

      eventHub.$emit('redirect', 'create');
    },
  },
};
</script>

<template lang='pug'>
nav.left-nav(:class='{ "left-nav--open": navMenuOpen }')
  .left-nav__menu(:class='{ "left-nav__menu--open": navMenuOpen }')
    a.left-nav__link(
      v-if='hasPrivilege(privileges.CAMPAIGNS)',
      @click='handleClickCreateCampaign',
    )
      .left-nav__link-icon.mdi.mdi-pencil-box-outline
      .left-nav__link-text CREATE
    a.left-nav__link(
      v-if='hasPrivilege(privileges.CAMPAIGNS)',
      :class='{\
        "left-nav__link--active": get($route, "matched[0].name") === "campaigns",\
        "left-nav__link--disabled": processing\
      }',
      @click='navigate("campaigns")',
    )
      .left-nav__link-icon.icon-campaigns
      .left-nav__link-text CAMPAIGNS
    a.left-nav__link(
      v-if='hasPrivilege(privileges.ORDERS)',
      :class='{\
        "left-nav__link--active": get($route, "matched[0].name") === "orders",\
        "left-nav__link--disabled": processing\
      }',
      @click='navigate("orders")',
    )
      .left-nav__link-icon.mdi.mdi-cart
      .left-nav__link-text ORDERS
    a.left-nav__link(
      v-if='hasPrivilege(privileges.PROMOTIONS)',
      :class='{\
        "left-nav__link--active": get($route, "matched[0].name") === "promotions",\
        "left-nav__link--disabled": processing\
      }',
      @click='navigate("promotions")',
    )
      .left-nav__link-icon.mdi.mdi-ticket-percent
      .left-nav__link-text COUPONS
    a.left-nav__link(
      v-if='hasPrivilege(privileges.STORES)',
      :class='{\
        "left-nav__link--active": get($route, "matched[0].name") === "stores",\
        "left-nav__link--disabled": processing\
      }',
      @click='navigate("stores")',
    )
      .left-nav__link-icon.icon-collections
      .left-nav__link-text COLLECTIONS
    a.left-nav__link(
      v-if='hasPrivilege(privileges.ACADEMY)',
      href='http://sellers.viralstyle.com',
      target='_blank',
      @click='closeMenu',
    )
      .left-nav__link-icon.mdi.mdi-school
      .left-nav__link-text ACADEMY
    a.left-nav__link(
      v-if='permissionViewPayouts',
      :class='{\
        "left-nav__link--active": get($route, "matched[1].name") === "settingsPayouts",\
        "left-nav__link--disabled": processing\
      }',
      @click='navigate("settingsPayouts")',
    )
      .left-nav__link-icon.mdi.mdi-cash
      .left-nav__link-text PAYOUTS
    a.left-nav__link(
      v-if='permissionAccessDomains',
      :class='{\
        "left-nav__link--active": get($route, "matched[0].name") === "domains",\
        "left-nav__link--disabled": processing\
      }',
      @click='navigate("domains")',
    )
      .left-nav__link-icon.mdi.mdi-store
      .left-nav__link-text DOMAINS
    a.left-nav__link(
      v-if='hasPrivilege(privileges.SETTINGS_NAV)',
      :class='{\
        "left-nav__link--active": get($route, "matched[0].name") === "settings" && get($route, "matched[1].name") !== "settingsPayouts",\
        "left-nav__link--disabled": processing\
      }',
      @click='navigate("settings")',
    )
      .left-nav__link-icon.mdi.mdi-settings
      .left-nav__link-text SETTINGS
    a.left-nav__link.left-nav__mobile(v-if='showMyOrdersLink', target='_blank', :href='profileUrl')
      .left-nav__link-icon.mdi.mdi-cart
      .left-nav__link-text MY ORDERS
    a.left-nav__link.left-nav__mobile(@click='handleLogout')
      .left-nav__link-icon.mdi.mdi-logout
      .left-nav__link-text LOG OUT

  .left-nav__block(:class='{ "left-nav__block--open": navMenuOpen }', @click='closeMenu')
</template>

<style>
@import '~import/index';
@import '~styles/index';

.left-nav {
  @extend %left-nav-width;
  @include col(sm, 12, true);
  order: 2;
  position: fixed;
  width: 100%;
  top: 50px;
  bottom: 0;
  z-index: 100;
  overflow-y: auto;
  visibility: hidden;
  transition: visibility 200ms;

  @include mq(mf-md) {
    visibility: visible;
    overflow: hidden;
    order: 3;
    height: auto;
    position: static;
    width: 130px;
  }

  &--open {
    visibility: visible;
  }

  &__menu {
    left: -18rem;
    width: 18rem;
    transition: left 200ms;
    position: absolute;
    background-color: $leftNavBgColor;
    z-index: 2;
    min-height: 100%;

    &--open {
      left: 0;
    }

    @include mq(mf-md) {
      position: static;
      width: 100%;
    }
  }

  &__block {
    visibility: hidden;
    width: 100%;
    position: fixed;
    top: 50px;
    left: 0;
    bottom: 0;
    z-index: 1;
    background-color: rgba(0, 0, 0, .3);
    opacity: 0;
    transition: opacity 200ms;

    &--open {
      visibility: visible;
      opacity: 1;
    }

    @include mq(mf-md) {
      display: none;
    }
  }

  &__link {
    @extend %flex;
    @extend %flex--row;
    @extend %flex--middle;
    @extend %flex--xs-left;
    @extend %flex--md-center;
    @extend %flex--md-column;
    @extend %transition-200;
    cursor: pointer;
    color: $leftNavFontColor;
    line-height: 1;
    padding: .75rem 1.5rem;

    &:first-child {
      padding-top: 1.5rem;
    }

    &:last-child {
      padding-bottom: 1.5rem;
    }

    @include mq(mf-md) {
      padding: 1.5rem;

      &:first-child {
        padding-top: 3rem;
      }

      &:last-child {
        padding-bottom: 3rem;
      }
    }

    &--active,
    &:hover {
      color: $white;
    }

    &--disabled {
      opacity: $disabledOpacity;

      &:hover {
        color: $leftNavFontColor;
      }
    }
  }

  &__link-icon {
    margin-top: 0;
    font-size: $h4;
    margin-right: 10px;

    @include mq(mf-md) {
      font-size: $h3;
      margin-right: 0;
    }
  }

  &__link-text {
    @extend %letter-spacing-1;
    font-size: $h5;

    @include mq(mf-md) {
      padding-top: 1rem;
      font-size: 1em;
    }
  }

  &__mobile {
    @extend %hidden--md-up;
  }

  @include mq(mf-md) {
    &-enter-active,
    &-leave-active {
      transition: all 500ms;
    }

    &-enter,
    &-leave-to {
      transform: translateX(-130px);
    }

    &-enter-to,
    &-leave {
      transform: translateX(0px);
    }
  }
}
</style>